/**
 * useCoPilotDraft - Session-scoped draft persistence for chat input
 * 
 * Stores draft in sessionStorage, keyed by context type and ID.
 * Supports debounced saving to avoid excessive writes.
 */
import { useState, useCallback, useRef, useEffect } from 'react';

export const useCoPilotDraft = (contextType, contextId) => {
  const draftSaveTimeoutRef = useRef(null);
  const [draft, setDraft] = useState('');

  // Build storage key
  const getDraftKey = useCallback(() => {
    if (!contextType || !contextId) return null;
    return `sr_copilot_draft_${contextType}_${contextId}`;
  }, [contextType, contextId]);

  // Load draft from storage
  const loadDraft = useCallback(() => {
    const key = getDraftKey();
    if (!key) return '';
    try {
      return sessionStorage.getItem(key) || '';
    } catch {
      return '';
    }
  }, [getDraftKey]);

  // Save draft to storage
  const saveDraft = useCallback((text) => {
    const key = getDraftKey();
    if (!key) return;
    try {
      if (text?.trim()) {
        sessionStorage.setItem(key, text);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore storage errors
    }
  }, [getDraftKey]);

  // Clear draft from storage
  const clearDraft = useCallback(() => {
    const key = getDraftKey();
    if (!key) return;
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore storage errors
    }
  }, [getDraftKey]);

  // Load on mount/context change
  useEffect(() => {
    setDraft(loadDraft());
  }, [loadDraft]);

  // Debounced update handler
  const updateDraft = useCallback((text) => {
    setDraft(text);
    
    // Clear existing timeout
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
    
    // Debounce save
    draftSaveTimeoutRef.current = setTimeout(() => {
      saveDraft(text);
    }, 500);
  }, [saveDraft]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
    };
  }, []);

  return {
    draft,
    updateDraft,
    clearDraft,
    loadDraft
  };
};

export default useCoPilotDraft;
